<template>
  <main id="main-content">
    <ApiLoading v-if="apiStatus.refData.loading || apiStatus.refData.fail" :apiError="apiStatus.refData.fail" />
    <div class="feature-landing-page" v-else>
      <div class="content-page-banner">
        <div class="banner-content">
          <h1>{{ $route.meta.title }}</h1>
          <div class="underline"></div>
          <div v-html="refData.addARebateSubtext.informationText" />
        </div>
      </div>
      <Alert v-if="submitPolicyError" :message="ErrorStrings.SUBMIT_POLICY_API_ERROR" alertType="error" />
      <section class="multi-step-form">
        <MultiStepFormNavigation
          :currentPage="currentPage"
          :links="navLinks"
          @linkClicked="updateStep"
          :disabled="apiStatus.submit.loading || apiStatus.submit.fail || apiStatus.premium.loading"
        />
        <ApiLoading v-if="apiStatus.submit.loading || apiStatus.submit.fail || apiStatus.premium.loading" :apiError="apiStatus.submit.fail" />
        <div v-else id="form-contents">
          <template v-if="currentPage == 1">
            <h2>Add a rebate</h2>
            <p>* indicates required field</p>
          </template>
          <Alert v-if="isContributorOrDependant" alertType="error" :message="refData.notCoveredAdultErrorText.informationText" />
          <template v-else>
            <keep-alive>
            <RebateForm
              v-if="currentPage === 1"
              :rebateTierInfo="rebateTierOptions"
              :rebateApplicants="rebateApplicant"
              :coverComposition="membershipInfo.composition"
              :rebateFormData="formResponse"
              :myCircumstances="''"
              :rebateTierLevel="formResponse.rebateTier"
              @rebateFormUpdated="updateFormResponse"
              :showRebateSectionHeader="false"
              :showRequiredFieldsText="false"
              :disableRebateApplicantDropdown="true"
              :rebateApplicantFieldHelperText="refData.rebateApplicantCalloutText.informationText"
              :medicareCardSectionSubText="refData.medicareCardInformationText.informationText"
              :householdIncomeFieldHelperText="refData.incomeTierCalloutText.informationText"
              ref="rebateForm"
            >
              <template #pageTitle>
                <h3 class="h6 sub-heading-underlined">Rebate applicant details</h3>
              </template>
              <template #privacyStatement>
                <p v-html="refData.privacyStatementInformationText.informationText" />
              </template>
            </RebateForm>
            </keep-alive>
            <keep-alive>
            <ReviewAndSubmitComponent v-if="currentPage === 2" :updateKey="false">
              <h3 class="review-subheading">Rebate applicant details</h3>
              <table class="review-and-submit-table rebate-applicant-details-table">
                <tbody>
                  <tr>
                    <th>Are you covered by this policy?</th>
                    <td>{{ formResponse.isPolicyCovered ? 'Yes' : 'No' }}</td>
                  </tr>
                  <tr>
                    <th>Do you want to apply a rebate?</th>
                    <td>{{ formResponse.isRebateApplied ? 'Yes' : 'No' }}</td>
                  </tr>
                  <tr>
                    <th>Are all people on the policy eligible for Medicare?</th>
                    <td>{{ formResponse.isMedicareEligible ? 'Yes' : 'No' }}</td>
                  </tr>
                  <tr>
                    <th>Rebate applicant</th>
                    <td>{{ `${rebateApplicant[0].firstName} ${rebateApplicant[0].lastName}` }}</td>
                  </tr>
                  <tr>
                    <th>Rebate reduction commencement date</th>
                    <td>{{ formResponse.currentDate }}</td>
                  </tr>
                </tbody>
              </table>

              <h3 class="review-subheading">Medicare card</h3>
              <table class="review-and-submit-table medicare-card-table">
                <tbody>
                  <tr>
                    <th>Medicare Card type</th>
                    <td>{{ formResponse.medicareCard.cardType }}</td>
                  </tr>
                  <tr>
                    <th>Medicare Card number</th>
                    <td>{{ formResponse.medicareCard.cardNumber }}</td>
                  </tr>
                  <tr>
                    <th>First name as it appears on your Medicare Card</th>
                    <td>{{ formResponse.medicareCard.firstName }}</td>
                  </tr>
                  <tr>
                    <th>Last name as it appears on your Medicare Card</th>
                    <td>{{ formResponse.medicareCard.lastName }}</td>
                  </tr>
                  <tr v-if="formResponse.medicareCard.expiryDate">
                    <th>Valid to date on Medicare Card</th>
                    <td>{{ formResponse.medicareCard.expiryDate }}</td>
                  </tr>
                </tbody>
              </table>

              <h3 class="review-subheading">Rebate tier information</h3>
              <table class="review-and-submit-table">
                <tbody>
                  <tr>
                    <th>My circumstances</th>
                    <td>{{ MyCircumstancesDisplay[formResponse.myCircumstances.toUpperCase()] }}</td>
                  </tr>
                  <tr>
                    <th>My household income for determining rebate is</th>
                    <td>
                      {{ `${RebateTierDisplayWithoutIncomeBrackets[formResponse.rebateTier]} - ${getSelectedIncomeBracketDisplay()}` }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <input type="checkbox" id="rebateAcceptance" checked disabled />
                      <label for="rebateAcceptance">
                        Do you declare that the information that you have provided is complete and correct?
                        <span class="required-input">*</span>
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <input type="checkbox" id="rebateAcceptance" checked disabled />
                      <label for="rebateAcceptance">
                        Do you understand that giving false or misleading information is a serious offence?
                        <span class="required-input">*</span>
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <th>Declaration date</th>
                    <td>{{ formResponse.currentDate }}</td>
                  </tr>
                </tbody>
              </table>

              <template v-if="currentPremium && newEstimatedPremium && apiStatus.premium.fail === false">
                <h3 class="review-subheading">My Premium</h3>
                <table class="review-and-submit-table">
                  <tbody>
                    <tr>
                      <th>Current premium</th>
                      <td>{{ `${formatAsCurrency(currentPremium)} / ${mapPaymentFrequencyFromRecurringToSingleton(membershipInfo.frequency)}` }}</td>
                    </tr>
                    <tr class="info">
                      <th>New premium</th>
                      <td>{{ `${formatAsCurrency(newEstimatedPremium)} / ${mapPaymentFrequencyFromRecurringToSingleton(membershipInfo.frequency)}` }}</td>
                    </tr>
                  </tbody>
                </table>
              </template>
              <template v-else>
                <Alert
                  alertType="info"
                  message="We are unable to advise you of your estimated new premium. If you would still like to proceed with the change, your new premium will be sent to you in your certificate of cover."
                />
              </template>
            </ReviewAndSubmitComponent>
            </keep-alive>
          </template>

          <div class="form-page-nav">
            <section class="form-buttons">
              <button id="back-button" v-if="currentPage !== 1" class="secondary" @click="handleBackButton">Back</button>
              <button id="next-button" @click.prevent="handleNextButton" :disabled="!isFormValid">
                {{ currentPage === 1 ? 'Next' : 'Add rebate' }}
              </button>
            </section>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { RebateForm, MultiStepFormNavigation, ReviewAndSubmitComponent, ApiLoading, Alert } from '@/components';
import { MyCircumstancesDisplay } from '@general/js/enums';
import { RebateTierDisplayWithoutIncomeBrackets } from '@/enums';
import { formatFormDate, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError } from '@general/js/utils';
import { MedicareCardType } from '@general/js/enums/MedicareCardTypeEnum';
import { mapMedicareCardCode } from '@/utils/CRMUtils';
import RebateServices from '@/services/MyCover/Rebates';
import { REBATES_MEMBER_PORTAL } from '@/router/routeNames';
import { convertFormToPdfDataUri } from '@/utils';
import CommonServices from '@general/js/services/Common';
import CommonServicesMP from '@/services/Common';
import { REBATE_FORM_PDF_FILENAMES } from '@general/js/_constants';
import { ErrorStrings } from '@general/js/constants';
import RebateMixins from './RebateMixins';

export default {
  name: 'AddRebateComponent',
  components: { RebateForm, MultiStepFormNavigation, ReviewAndSubmitComponent, ApiLoading, Alert },
  mixins: [RebateMixins],

  data() {
    return {
      currentPage: 1,
      isFormValid: false,
      apiStatus: {
        refData: { loading: false, fail: false },
        submit: { loading: false, fail: false },
        premium: { loading: false, fail: false },
      },
      submitPolicyError: false,
      pages: {
        REBATE_DETAILS: {
          pageTitle: 'Add a rebate',
          pageNumber: 1,
          isInvalid: false,
        },
        REVIEW_AND_SUBMIT: {
          pageTitle: 'Review and submit',
          pageNumber: 2,
          isInvalid: true,
        },
      },
      formResponse: {
        isRebateApplied: null,
        isPolicyCovered: null,
        isMedicareEligible: null,
        medicareCard: null,
        myCircumstances: null,
        rebateApplicant: null,
        rebateTier: null,
        rebateDeclaration: false,
        rebateAcceptance: false,
      },
      refData: {},
      rebateFormPdf: null,
      MyCircumstancesDisplay,
      RebateTierDisplayWithoutIncomeBrackets,
      ErrorStrings,
    };
  },

  watch: {
    isFormValid() {
      this.pages.REVIEW_AND_SUBMIT.isInvalid = !this.isFormValid;
    },
    async currentPage(newVal, oldVal) {
      if (newVal === this.pages.REVIEW_AND_SUBMIT.pageNumber) {
        this.fetchNewPremium(this.formResponse.rebateTier, this.apiStatus.premium);
      }
      if (oldVal === this.pages.REBATE_DETAILS.pageNumber && newVal !== oldVal) {
        // Need to save rebate form every time user goes away from step 1 to capture latest updated form data
        this.rebateFormPdf = await convertFormToPdfDataUri(this.$refs.rebateForm);
      }
    },
  },

  beforeMount() {
    this.fetchRefData();
  },

  computed: {
    rebateApplicant() {
      // Rebate applicant can only be currently logged in member
      return [
        {
          memberNumber: this.memberNumber,
          personCode: this.currentMemberDetails.personCode,
          firstName: this.currentMemberDetails.firstName,
          lastName: this.currentMemberDetails.lastName,
        },
      ];
    },
  },

  methods: {
    async fetchRefData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;
      try {
        this.refData = await RebateServices.getAddRebateRefData();
      } catch (error) {
        this.apiStatus.refData.fail = true;
        logger.log('Error fetching add rebate ref data', error);
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    async submitAddRebate() {
      this.apiStatus.submit.loading = true;
      this.apiStatus.submit.fail = false;
      this.submitPolicyError = false;
      this.updateSubmitPolicyInProcess(false);
      try {
        const payload = {
          rebateTier: this.formResponse.rebateTier,
          currentDate: formatFormDate(this.formResponse.currentDate),
          medicareCard: {
            cardType: mapMedicareCardCode(this.formResponse.medicareCard.cardType),
            medicareCardNumber: this.formResponse.medicareCard.cardNumber.replace(/\s/g, ''),
            firstName: this.formResponse.medicareCard.firstName,
            lastName: this.formResponse.medicareCard.lastName,
          },
        };

        if (this.formResponse.medicareCard.cardType !== MedicareCardType.GREEN && this.formResponse.medicareCard.expiryDate !== null) {
          payload.medicareCard.expiryDate = formatFormDate(this.formResponse.medicareCard.expiryDate);
        }

        const response = await RebateServices.submitRebateRegistration(this.memberNumber, payload);

        await CommonServices.uploadRebateFormPdf(this.memberNumber, REBATE_FORM_PDF_FILENAMES.MEMBER_PORTAL_ADD_REBATE, this.rebateFormPdf);

        const params = {
          newPremium: response.newPremium,
          currentPayFrequency: response.currentPayFrequency,
          premiumChangeInformationText: this.refData.premiumInformationInformationText.informationText,
          premiumAdjustmentInformationText: this.refData.premiumAdjustmentInformationText.informationText,
        };

        this.navigateToPage(REBATES_MEMBER_PORTAL.REBATE_UPDATE_SUCCESS, params);
      } catch (error) {
        logger.error(error);
        if (error instanceof SubmitPolicyError) {
          this.submitPolicyError = true;
        }
        if (error instanceof SubmitPolicyInProcessError || error instanceof SubmitPolicyTimeoutError) {
          this.updateSubmitPolicyInProcess(true);

          // Raise work item with payload only for timeout error
          if (error instanceof SubmitPolicyTimeoutError) {
            const payload = {
              rebateTier: this.formResponse.rebateTier,
              currentDate: formatFormDate(this.formResponse.currentDate),
              medicareCard: {
                cardType: mapMedicareCardCode(this.formResponse.medicareCard.cardType),
                medicareCardNumber: this.formResponse.medicareCard.cardNumber.replace(/\s/g, ''),
                firstName: this.formResponse.medicareCard.firstName,
                lastName: this.formResponse.medicareCard.lastName,
              },
            };

            const request = {
              functionName: 'add-update-rebate',
              workItemCategory: 'OTHERS',
              title: `Submission Timeout for ${this.$route?.params?.policyNumber} in Member Portal`,
              payload,
            };

            await CommonServicesMP.raiseWorkItem(this.$route?.params?.policyNumber, request);
          }

          // Redirect to success page
          const params = {
            submitPolicyInProcessHeader: ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.replacePlaceholder(ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.HEADER, 'add rebate'),
            submitPolicyInProcessSubHeader: ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.SUBHEADER,
          };

          this.navigateToPage(REBATES_MEMBER_PORTAL.REBATE_UPDATE_SUCCESS, params);
        } else {
          this.apiStatus.submit.fail = true;
        }
      } finally {
        this.apiStatus.submit.loading = false;
      }
    },

    handleNextButton() {
      if (this.currentPage === 1) {
        this.currentPage += 1;
      } else {
        this.submitAddRebate();
      }
    },

    handleBackButton() {
      this.currentPage -= 1;
    },

    updateFormResponse(response) {
      this.formResponse = { ...response?.data };
      this.isFormValid = response?.isFormValid;
    },

    updateStep(step) {
      this.currentPage = step;
    },

    getComponentName(step) {
      return Object.values(this.pages).find((page) => {
        return page.pageNumber === step;
      }).componentName;
    },

    getSelectedIncomeBracketDisplay() {
      return this.rebateTierOptions
        .find((option) => {
          return option.myCircumstance === this.formResponse.myCircumstances;
        })
        .rebateTierOptions.find((option) => {
          return option.dhl_code === this.formResponse.rebateTier;
        })?.dhl_name;
    },
  },
};
</script>

<style lang="scss" scoped></style>
